import { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { BookOpen, ShoppingBag, Heart, Clock, ArrowLeft, Package, Copy, Shield, Truck, Plus, IndianRupee, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const CustomerDashboard = () => {
  const { user, token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'profile' || searchParams.get('tab') === 'cart'
    ? searchParams.get('tab')
    : 'rentals';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [orders, setOrders] = useState([]);
  const [cart, setCart] = useState([]);
  const [stats, setStats] = useState({
    total_orders: 0,
    active_rentals: 0,
    total_spent: 0
  });
  const [loading, setLoading] = useState(true);
  const [deposit, setDeposit] = useState(null);
  const [showTopupDialog, setShowTopupDialog] = useState(false);
  const [topupAmount, setTopupAmount] = useState(500);
  const [showWithdrawDialog, setShowWithdrawDialog] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [returnShipmentLoading, setReturnShipmentLoading] = useState({});

  const handleRequestReturn = async (orderId) => {
    setReturnShipmentLoading(prev => ({ ...prev, [orderId]: true }));
    try {
      const res = await axios.post(
        `${API}/orders/${orderId}/shiprocket/return`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token') || token}` } }
      );
      toast.success(res.data?.message || 'Return shipment initiated with Shiprocket');
      fetchDashboardData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to initiate return shipment');
    } finally {
      setReturnShipmentLoading(prev => ({ ...prev, [orderId]: false }));
    }
  };

  useEffect(() => {
    fetchDashboardData();
    if (searchParams.get('addMoney') === '1') {
      setActiveTab('profile');
      setShowTopupDialog(true);
    }
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [ordersRes, cartRes, depRes] = await Promise.all([
        axios.get(`${API}/orders`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/cart`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/user/deposit`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { balance: 0 } }))
      ]);
      
      setOrders(ordersRes.data);
      setCart(cartRes.data);
      setDeposit(depRes.data);
      
      const isPaidOrder = (order) =>
        order.payment_status === 'completed' &&
        !['payment_failed', 'pending', 'cancelled'].includes(order.status);

      const paidOrders = ordersRes.data.filter(isPaidOrder);
      const totalSpent = paidOrders.reduce(
        (sum, order) => sum + (Number(order.final_amount) || 0),
        0
      );
      const activeRentals = paidOrders.filter((order) =>
        ['paid', 'confirmed', 'shipped', 'delivered', 'ready_for_pickup', 'collected', 'paid_pending_lender_approval'].includes(order.status)
      ).length;

      setStats({
        total_orders: paidOrders.length,
        active_rentals: activeRentals,
        total_spent: totalSpent
      });
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleTopup = async () => {
    if (!topupAmount || topupAmount <= 0) { toast.error('Enter a valid amount'); return; }
    try {
      const res = await axios.post(`${API}/user/deposit/topup`, { amount: topupAmount }, { headers: { Authorization: `Bearer ${token}` } });
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => {
        const options = {
          key: process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_test_key',
          amount: res.data.amount,
          currency: 'INR',
          name: 'RentMyBook',
          description: `Add Money to Security Deposit: ₹${topupAmount}`,
          order_id: res.data.razorpay_order_id,
          handler: async (response) => {
            try {
              await axios.post(`${API}/user/deposit/verify`, {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                amount: res.data.amount
              }, { headers: { Authorization: `Bearer ${token}` } });
              toast.success(`₹${topupAmount} added to your security deposit successfully.`);
              setShowTopupDialog(false);
              window.dispatchEvent(new Event('deposit-updated'));
              fetchDashboardData();
            } catch { toast.error('Verification failed'); }
          },
          prefill: { name: user?.name, email: user?.email },
          theme: { color: '#C5A572' }
        };
        const rzp = new window.Razorpay(options);
        rzp.open();
      };
      document.body.appendChild(script);
    } catch (err) { toast.error(err.response?.data?.detail || 'Top-up failed'); }
  };

  const openWithdrawDialog = () => {
    const balance = Number(deposit?.balance || 0);
    const active = Number(deposit?.active_rentals ?? stats.active_rentals ?? 0);
    if (active > 0) {
      toast.error('Return all active rentals before withdrawing your security deposit.');
      return;
    }
    if (balance <= 0) {
      toast.error('No deposit balance to withdraw');
      return;
    }
    setWithdrawAmount(String(Math.floor(balance)));
    setShowWithdrawDialog(true);
  };

  const handleWithdraw = async () => {
    const amount = Number(withdrawAmount);
    if (!amount || amount <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    setWithdrawLoading(true);
    try {
      const res = await axios.post(
        `${API}/user/deposit/withdraw`,
        { amount },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data?.message || `₹${amount} withdrawal requested`);
      setShowWithdrawDialog(false);
      window.dispatchEvent(new Event('deposit-updated'));
      fetchDashboardData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Withdrawal failed');
    } finally {
      setWithdrawLoading(false);
    }
  };

  const beforePickupDeadline = (order) => {
    const raw = String(order?.rental_start_date || order?.start_date || '');
    const day = raw.slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return true;
    const hasClock = /T\d{2}:\d{2}/.test(raw) && !raw.includes('T00:00');
    const deadline = hasClock ? new Date(raw) : new Date(`${day}T23:59:59+05:30`);
    if (Number.isNaN(deadline.getTime())) return true;
    return Date.now() <= deadline.getTime();
  };

  const canCancelOrder = (order) => {
    if (!order) return false;
    if (['cancelled', 'returned', 'failed', 'payment_failed'].includes((order.status || '').toLowerCase())) return false;
    if (order.pickup_status === 'collected') return false;
    if (!beforePickupDeadline(order)) return false;
    return ['pending', 'paid', 'confirmed', 'processing', 'ready_for_pickup', 'paid_pending_lender_approval'].includes((order.status || '').toLowerCase())
      || order.payment_status === 'completed';
  };

  const handleCancelOrder = async (order) => {
    if (!canCancelOrder(order)) return;
    if (!window.confirm('Cancel this booking? You can cancel until pickup time for a full refund. After pickup time, cancellation is closed and there is no refund.')) {
      return;
    }
    try {
      const res = await axios.post(
        `${API}/orders/${order.id}/cancel`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token') || token}` } }
      );
      toast.success(res.data?.refund_note || 'Booking cancelled');
      fetchDashboardData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel booking');
    }
  };

  return (
    <div className="min-h-screen" style={{background: 'var(--background)'}}>
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-2" data-testid="customer-dashboard-title">My Rentals</h1>
        <p className="text-sm text-gray-600 mb-8 max-w-3xl">
          Books you have borrowed. My Copies is books you own and list. Transactions is money only (rent paid, deposit, withdrawals, earnings).
        </p>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg" style={{backgroundColor: '#E8E4C9'}}>
                <ShoppingBag className="h-6 w-6" style={{color: '#A89968'}} />
              </div>
            </div>
            <p className="text-gray-600 text-sm">Total Orders</p>
            <p className="text-3xl font-bold text-gray-900" data-testid="total-orders">{stats.total_orders}</p>
            <p className="text-xs text-gray-500 mt-1">All time orders</p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg" style={{backgroundColor: '#E8E4C9'}}>
                <Clock className="h-6 w-6" style={{color: '#A89968'}} />
              </div>
            </div>
            <p className="text-gray-600 text-sm">Active Rentals</p>
            <p className="text-3xl font-bold text-gray-900" data-testid="active-rentals">{stats.active_rentals}</p>
            <p className="text-xs text-gray-500 mt-1">Currently rented</p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg" style={{backgroundColor: '#E8E4C9'}}>
                <Package className="h-6 w-6" style={{color: '#A89968'}} />
              </div>
            </div>
            <p className="text-gray-600 text-sm">Total Spent</p>
            <p className="text-3xl font-bold text-gray-900" data-testid="total-spent">₹{Math.round(stats.total_spent)}</p>
            <p className="text-xs text-gray-500 mt-1">Lifetime spending</p>
          </div>
        </div>

        {/* Deposit */}
        <div className="grid grid-cols-1 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-md p-6">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <h3 className="font-bold text-lg flex items-center gap-2">
                <Shield className="h-5 w-5" style={{color: 'var(--accent)'}} />
                Security Deposit
              </h3>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => setShowTopupDialog(true)} data-testid="topup-deposit-btn">
                  <Plus className="h-4 w-4 mr-1" /> Add Money
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={openWithdrawDialog}
                  disabled={!(deposit?.balance > 0) || (deposit?.active_rentals ?? stats.active_rentals) > 0}
                  data-testid="withdraw-deposit-btn"
                  title={
                    (deposit?.active_rentals ?? stats.active_rentals) > 0
                      ? 'Not available while you have active rentals'
                      : 'Withdraw security deposit'
                  }
                >
                  <IndianRupee className="h-4 w-4 mr-1" /> Withdraw
                </Button>
              </div>
            </div>
            <p className="text-3xl font-bold" style={{color: 'var(--accent)'}}>₹{deposit?.available_balance ?? deposit?.balance ?? 0}</p>
            <p className="text-xs text-gray-500 mt-1">
              Wallet ₹{deposit?.balance || 0}
              {(deposit?.held_amount || 0) > 0
                ? ` · ₹${Math.round(deposit.held_amount)} held until the current rental is returned`
                : ''}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {deposit?.total_deposited > 0 
                ? `Total deposited: ₹${deposit.total_deposited} | Deducted: ₹${deposit.total_deducted || 0}`
                : 'Deposit will be collected at the time of your first rental.'}
            </p>
            <p className="text-xs text-gray-500 mt-2">
              Deducting or withdrawing only changes this wallet. It is not an instant bank transfer. Paid-out refunds are marked by the team and usually reach your bank in 7–10 business days.
            </p>
            {(deposit?.active_rentals ?? stats.active_rentals) > 0 && (
              <p className="text-xs text-amber-700 mt-2">
                Withdraw is unavailable while you have active rentals. Return all books first.
              </p>
            )}
          </div>
        </div>

        {/* Top-up Dialog */}
        <Dialog open={showTopupDialog} onOpenChange={setShowTopupDialog}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Add Money to Security Deposit</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-3 gap-2">
                {[300, 500, 1000].map(amt => (
                  <button key={amt} onClick={() => setTopupAmount(amt)}
                    className={`p-2 rounded-lg border text-sm font-semibold transition-colors ${topupAmount === amt ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-gray-200'}`}
                    data-testid={`topup-${amt}`}
                  >₹{amt}</button>
                ))}
              </div>
              <input
                type="number"
                min="1"
                value={topupAmount}
                onChange={(e) => setTopupAmount(parseInt(e.target.value) || 0)}
                className="w-full border rounded-lg p-2 text-center text-lg font-semibold"
                data-testid="topup-custom-amount"
              />
              <Button onClick={handleTopup} className="w-full" style={{backgroundColor: 'var(--primary)', color: 'white'}} data-testid="topup-confirm-btn">
                Pay ₹{topupAmount}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Withdraw Dialog */}
        <Dialog open={showWithdrawDialog} onOpenChange={setShowWithdrawDialog}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Withdraw Security Deposit</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <p className="text-sm text-gray-600">
                Available balance: <strong>₹{deposit?.balance || 0}</strong>. Refund to your original payment method usually takes 7–10 business days.
              </p>
              <input
                type="number"
                min="1"
                max={deposit?.balance || 0}
                value={withdrawAmount}
                onChange={(e) => setWithdrawAmount(e.target.value)}
                className="w-full border rounded-lg p-2 text-center text-lg font-semibold"
                data-testid="withdraw-amount-input"
              />
              <Button
                onClick={handleWithdraw}
                disabled={withdrawLoading}
                className="w-full"
                style={{backgroundColor: 'var(--primary)', color: 'white'}}
                data-testid="withdraw-confirm-btn"
              >
                {withdrawLoading ? 'Processing…' : `Withdraw ₹${withdrawAmount || 0}`}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6" style={{backgroundColor: 'white'}}>
            <TabsTrigger value="rentals" data-testid="rentals-tab">My Rentals</TabsTrigger>
            <TabsTrigger value="cart" data-testid="cart-tab">Cart ({cart.length})</TabsTrigger>
            <TabsTrigger value="profile" data-testid="profile-tab">Profile</TabsTrigger>
          </TabsList>

          {/* My Rentals Tab — MBR-120 */}
          <TabsContent value="rentals">
            <div className="bg-white rounded-2xl shadow-md p-6">
              <h2 className="text-2xl font-bold mb-1">My Rentals</h2>
              <p className="text-sm text-gray-500 mb-4">View active rentals, pickup codes, and cancel bookings per our policy.</p>
              <Button
                variant="outline"
                size="sm"
                className="mb-4"
                onClick={() => navigate('/transactions')}
                data-testid="rentals-transactions-link"
              >
                Transaction history
              </Button>
              {orders.length === 0 ? (
                <div className="text-center py-12">
                  <ShoppingBag className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600">No orders yet</p>
                  <Button 
                    onClick={() => navigate('/')}
                    className="mt-4 hover:opacity-90"
                    style={{backgroundColor: '#A89968'}}
                  >
                    Browse Books
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {orders.map((order) => (
                    <div key={order.id} className="border rounded-lg p-4" data-testid={`order-${order.id}`}>
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-semibold">Order #{order.id.slice(0, 8)}</p>
                          <p className="text-sm text-gray-600">{order.items.length} item(s)</p>
                          {/* Delivery Method Badge */}
                          <div className="flex items-center mt-1 space-x-2">
                            {order.delivery_method === 'pickup' ? (
                              <span className="inline-flex items-center text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-800">
                                <Shield className="h-3 w-3 mr-1" />
                                Self Pickup
                              </span>
                            ) : (
                              <span className="inline-flex items-center text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-800">
                                <Truck className="h-3 w-3 mr-1" />
                                Delivery
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                            order.status === 'payment_failed' || order.payment_status === 'failed'
                              ? 'bg-red-100 text-red-800'
                              : order.payment_status === 'completed'
                              ? 'bg-green-100 text-green-800'
                              : ''
                          }`} style={
                            order.status !== 'payment_failed' && order.payment_status !== 'failed' && order.payment_status !== 'completed'
                              ? { backgroundColor: '#E8E4C9', color: '#A89968' }
                              : undefined
                          }>
                            {order.status === 'payment_failed' || order.payment_status === 'failed'
                              ? 'Payment Failed'
                              : order.status === 'paid_pending_lender_approval' || order.lender_approval_status === 'pending'
                              ? 'Awaiting Lender'
                              : order.status === 'rejected_by_lender'
                              ? 'Declined'
                              : order.lender_approval_status === 'approved' || order.status === 'paid'
                              ? 'Lender Approved'
                              : order.payment_status === 'completed'
                              ? 'Paid'
                              : order.status}
                          </span>
                          {/* Pickup Status Badge — only for successfully paid orders */}
                          {order.delivery_method === 'pickup' &&
                            order.pickup_status &&
                            order.payment_status === 'completed' &&
                            order.status !== 'payment_failed' && (
                            <div className="mt-1">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                order.pickup_status === 'collected' ? 'bg-green-100 text-green-800' :
                                order.pickup_status === 'ready' ? 'bg-blue-100 text-blue-800' :
                                'bg-yellow-100 text-yellow-800'
                              }`}>
                                {order.pickup_status === 'collected' ? '✓ Collected' :
                                 order.pickup_status === 'ready' ? '📦 Ready' :
                                 '⏳ Preparing'}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Awaiting lender — clear borrower status */}
                      {(order.status === 'paid_pending_lender_approval' || order.lender_approval_status === 'pending') && (
                        <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200 text-sm text-amber-900">
                          Waiting for the book lender to approve. You will get a WhatsApp/email when they approve or decline — and your pickup code will appear here after approval.
                        </div>
                      )}

                      {/* Pickup Code — only after lender approval (or when approval not required) */}
                      {order.delivery_method === 'pickup' &&
                        order.pickup_code &&
                        order.payment_status === 'completed' &&
                        order.status !== 'paid_pending_lender_approval' &&
                        order.lender_approval_status !== 'pending' &&
                        order.status !== 'rejected_by_lender' && (
                        <div className="mt-3 p-3 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-amber-200">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <Shield className="h-4 w-4 text-amber-600" />
                              <span className="text-sm font-medium text-amber-800">Pickup Code:</span>
                              <span className="font-mono font-bold text-lg" style={{color: 'var(--primary)'}} data-testid={`pickup-code-${order.id}`}>
                                {order.pickup_code}
                              </span>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                navigator.clipboard.writeText(order.pickup_code);
                                toast.success('Pickup code copied!');
                              }}
                              className="text-amber-600 hover:text-amber-700"
                              data-testid={`copy-code-${order.id}`}
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          </div>
                          {order.pickup_status === 'ready' && (
                            <p className="text-xs text-green-600 mt-1">
                              ✓ Your book is ready for pickup! Show this code to collect.
                            </p>
                          )}
                        </div>
                      )}

                      {order.delivery_method === 'delivery' && (
                        <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200" data-testid={`shiprocket-${order.id}`}>
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                            <div>
                              {order.awb_code && (
                                <p className="text-sm font-medium text-blue-900 flex items-center gap-1">
                                  <Truck className="h-4 w-4" />
                                  {order.courier_name || 'Courier'}: AWB {order.awb_code}
                                </p>
                              )}
                              {order.return_awb_code ? (
                                <div className="mt-1 flex flex-wrap items-center gap-2">
                                  <span className="text-xs font-semibold text-green-700">Return Pickup Scheduled:</span>
                                  <span className="text-xs text-blue-800">AWB {order.return_awb_code}</span>
                                  {order.return_tracking_url && (
                                    <a
                                      href={order.return_tracking_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-xs font-semibold text-blue-700 hover:underline"
                                    >
                                      Track Return
                                    </a>
                                  )}
                                </div>
                              ) : (
                                ['delivered', 'confirmed', 'shipped'].includes((order.status || '').toLowerCase()) && (
                                  <div className="mt-2">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="bg-white border-blue-300 text-blue-700 hover:bg-blue-50 text-xs h-8"
                                      onClick={() => handleRequestReturn(order.id)}
                                      disabled={returnShipmentLoading[order.id]}
                                      data-testid={`request-return-${order.id}`}
                                    >
                                      {returnShipmentLoading[order.id] ? (
                                        <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Scheduling Return...</>
                                      ) : (
                                        <><Truck className="h-3 w-3 mr-1" /> Schedule Return Pickup (Shiprocket)</>
                                      )}
                                    </Button>
                                  </div>
                                )
                              )}
                            </div>
                            {order.tracking_url && (
                              <a
                                href={order.tracking_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm font-semibold text-blue-700 hover:underline"
                              >
                                Track shipment
                              </a>
                            )}
                          </div>
                        </div>
                      )}
                      {order.delivery_method === 'delivery' && !order.awb_code && order.shiprocket_error && (
                        <p className="mt-2 text-xs text-amber-700">
                          Shipping pending: {order.shiprocket_error}
                        </p>
                      )}
                      
                      <div className="flex justify-between items-center pt-2 border-t mt-3 gap-3">
                        <div>
                          <span className="text-gray-600">Total Amount:</span>
                          <span className="font-bold text-lg ml-2" style={{color: '#A89968'}}>₹{Math.round(order.final_amount)}</span>
                        </div>
                        {canCancelOrder(order) && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => handleCancelOrder(order)}
                            data-testid={`cancel-booking-${order.id}`}
                          >
                            Cancel Booking
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Cart Tab */}
          <TabsContent value="cart">
            <div className="bg-white rounded-2xl shadow-md p-6">
              <h2 className="text-2xl font-bold mb-4">Shopping Cart</h2>
              {cart.length === 0 ? (
                <div className="text-center py-12">
                  <ShoppingBag className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600">Your cart is empty</p>
                  <Button 
                    onClick={() => navigate('/')}
                    className="mt-4 hover:opacity-90"
                    style={{backgroundColor: '#A89968'}}
                  >
                    Browse Books
                  </Button>
                </div>
              ) : (
                <>
                  <div className="space-y-4 mb-6">
                    {cart.map((item) => (
                      <div key={item.id} className="flex items-center space-x-4 border rounded-lg p-4">
                        <div className="w-16 h-20 rounded" style={{backgroundColor: 'var(--card)'}}>
                          {item.book.cover_image ? (
                            <img src={item.book.cover_image} alt={item.book.title} className="w-full h-full object-cover rounded" />
                          ) : (
                            <BookOpen className="w-full h-full p-3" style={{color: 'var(--primary)'}} />
                          )}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-bold">{item.book.title}</h4>
                          <p className="text-sm text-gray-600">{item.weeks} week(s)</p>
                          <p className="font-bold mt-1" style={{color: '#A89968'}}>
                            ₹{Math.round(item.book.rental_price_per_week * item.weeks)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Button 
                    onClick={() => navigate('/cart')}
                    className="w-full hover:opacity-90"
                    style={{backgroundColor: '#A89968'}}
                  >
                    Proceed to Checkout
                  </Button>
                </>
              )}
            </div>
          </TabsContent>

          {/* Profile Tab — MBR-121 */}
          <TabsContent value="profile">
            <div className="space-y-6">
              <div className="bg-white rounded-2xl shadow-md p-6">
                <h2 className="text-2xl font-bold mb-6">My Profile</h2>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Name</p>
                      <p className="font-semibold">{user?.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Email</p>
                      <p className="font-semibold">{user?.email || '—'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Phone</p>
                      <p className="font-semibold">{user?.phone}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Pincode</p>
                      <p className="font-semibold">{user?.pincode}</p>
                    </div>
                  </div>
                  {user?.address && (
                    <div>
                      <p className="text-sm text-gray-600">Address</p>
                      <p className="font-semibold">{user.address}</p>
                      <p className="font-semibold">{user.city}, {user.state}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-md p-6" data-testid="profile-deposit-section">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-bold flex items-center gap-2">
                    <Shield className="h-5 w-5" style={{color: 'var(--accent)'}} />
                    Security Deposit
                  </h3>
                  <Button size="sm" onClick={() => setShowTopupDialog(true)} style={{backgroundColor: 'var(--primary)', color: 'white'}} data-testid="profile-add-money-btn">
                    <Plus className="h-4 w-4 mr-1" /> Add Money
                  </Button>
                </div>
                <p className="text-3xl font-bold" style={{color: 'var(--accent)'}} data-testid="profile-deposit-balance">₹{deposit?.available_balance ?? deposit?.balance ?? 0}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Wallet ₹{deposit?.balance ?? 0}
                  {(deposit?.held_amount || 0) > 0
                    ? ` · ₹${Math.round(deposit.held_amount)} held until the book is returned`
                    : ''}
                  {' '}· Deposited ₹{deposit?.total_deposited ?? 0} · Deducted ₹{deposit?.total_deducted ?? 0}
                </p>
              </div>

              <div className="bg-white rounded-2xl shadow-md p-6" data-testid="transaction-history-section">
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <IndianRupee className="h-5 w-5" style={{color: 'var(--primary)'}} />
                  Transaction History
                </h3>
                {(deposit?.transactions || []).length === 0 && orders.filter(o => o.payment_status === 'completed').length === 0 ? (
                  <p className="text-sm text-gray-500">No transactions yet.</p>
                ) : (
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {(deposit?.transactions || []).map((t) => (
                      <div key={t.id || `${t.type}-${t.created_at}`} className="flex justify-between items-center border rounded-lg p-3 text-sm">
                        <div>
                          <p className="font-medium capitalize">{t.type || 'deposit'}</p>
                          <p className="text-xs text-gray-500">{t.reason || t.description || '—'}</p>
                          <p className="text-xs text-gray-400">{t.created_at ? new Date(t.created_at).toLocaleString() : ''}</p>
                        </div>
                        <span className={`font-bold ${['credit', 'topup', 'refund'].includes(t.type) ? 'text-green-600' : 'text-red-600'}`}>
                          {['credit', 'topup', 'refund'].includes(t.type) ? '+' : '-'}₹{t.amount}
                        </span>
                      </div>
                    ))}
                    {orders.filter(o => o.payment_status === 'completed').map((o) => (
                      <div key={`order-${o.id}`} className="flex justify-between items-center border rounded-lg p-3 text-sm">
                        <div>
                          <p className="font-medium">Rental payment</p>
                          <p className="text-xs text-gray-500">Order #{o.id.slice(0, 8)} · {o.status}</p>
                          <p className="text-xs text-gray-400">{o.created_at ? new Date(o.created_at).toLocaleString() : ''}</p>
                        </div>
                        <span className="font-bold text-gray-800">-₹{Math.round(o.final_amount)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
      <Footer />
    </div>
  );
};

export default CustomerDashboard;
